import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FinancialBreakdownController } from "services/financialBreakdown/controller";
import { AccordionCard, Button, useAccordionWizard } from "stories";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useToast } from "utils/customHooks/useToast";
import { SourcesOfFundsTable } from "./SourcesOfFunds";
import { usesRowsProps, UsesTable } from "./UsesTable";

const financialBreakdown = FinancialBreakdownController();
export const SourcesAndUsesOfFunds = forwardRef((props, ref) => {
  const { next } = useAccordionWizard();
  const form = useForm();
  const { showToast } = useToast();
  const { id: opportunityId } = useParams();
  const [sourcesAndUses, getSourcesAndUses] = useFetchState();
  const [updateSourcesAndUsesState, updateSourcesAndUses] = useFetchState();
  const [equalCalculation, setEqualCalculation] = useState(false);
  useEffect(() => {
    getSourcesAndUses(financialBreakdown.getSourcesAndUses, opportunityId);
  }, []); // eslint-disable-line
  useImperativeHandle(ref, () => ({
    refetch() {
      getSourcesAndUses(financialBreakdown.getSourcesAndUses, opportunityId);
    },
  }));
  const uses = useWatch({
    control: form.control,
    name: "uses",
  });
  useEffect(() => {
    if (
      !sourcesAndUses.isError &&
      !sourcesAndUses.isLoading &&
      sourcesAndUses.data
    ) {
      const usesRows = sourcesAndUses.data.uses;
      if (Array.isArray(usesRows.other_uses))
        usesRows.other_uses = usesRows.other_uses.map((row) => {
          return {
            ...row,
            props: usesRowsProps,
          };
        });
      if (!usesRows.other_uses) usesRows.other_uses = [];
      form.reset({
        ...sourcesAndUses.data,
        uses: usesRows,
      });
    }
  }, [sourcesAndUses]); // eslint-disable-line
  const save = (values) => {
    const body = {
      other_uses: values.uses.other_uses.map((data) => {
        return {
          title: data.title,
          amount: Number(parseFloat(data.amount)),
          id: data.id,
        };
      }),
      interest_reserve: Number(parseFloat(values.uses.interest_reserve)),
      equity_takeout: Number(parseFloat(values.uses.equity_takeout)),
      id: opportunityId,
    };
    updateSourcesAndUses(financialBreakdown.updateSourcesAndUses, body);
  };
  useEffect(() => {
    if (
      !updateSourcesAndUsesState.isError &&
      !updateSourcesAndUsesState.isLoading &&
      updateSourcesAndUsesState.data === ""
    ) {
      showToast("Sources and Uses of funds updated successfuly", "success");
      next();
      if (props.onSave) props.onSave();
    }
  }, [updateSourcesAndUsesState]); // eslint-disable-line
  return (
    <div className={"mt-4"} ref={ref}>
      <AccordionCard
        headerText="Sources and Uses of Funds"
        accordionKey="sources-funds"
      >
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(save)}>
            {!equalCalculation && (
              <span className="text-red mb-2 d-block">
                Error: total uses of funds do not equal total sources of funds
              </span>
            )}
            {uses && <UsesTable equalCalculation={equalCalculation} />}
            <div className="mt-4">
              {uses && (
                <SourcesOfFundsTable
                  equalCalculation={equalCalculation}
                  setEqualCalculation={setEqualCalculation}
                />
              )}
            </div>
            <Row className={"mt-5"}>
              <Col />
              <Col xs={2}>
                <Button
                  label={"Save and Next"}
                  loading={updateSourcesAndUsesState.isLoading}
                />
              </Col>
            </Row>
          </form>
        </FormProvider>
      </AccordionCard>
    </div>
  );
});
